import './PhoneInput.scss';

import { Input, Select, Space } from 'antd';
import Flags from 'country-flag-icons/react/3x2';
import { SUPPORTED_PREFIX } from 'helpers/contactPersonHelpers';
import { useValidators } from 'helpers/validation';
import React from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { InputNoteConfig } from '../_common';
import {
  getHookFieldClass,
  getHookGroupClass,
  renderErrorMessage,
  renderHookInputHeader
} from '../_common';

interface PhoneInputProps<T extends FieldValues> {
  name: Path<T>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  inputNote?: InputNoteConfig;
  hideLabel?: boolean;
  customOnBlur?: () => void;
}

function PhoneInput<T extends FieldValues = any>({
  name,
  label,
  placeholder,
  required,
  disabled,
  inputNote,
  hideLabel,
  customOnBlur
}: PhoneInputProps<T>) {
  const validators = useValidators();
  const { control, getFieldState, formState } = useFormContext<T>();

  const { invalid, isDirty, error, isTouched } = getFieldState(name, formState);

  const showError = !!error && isDirty;

  const renderPrefixLabel = (
    FlagIcon: React.ComponentType<{ className?: string }>,
    prefix: string
  ) => (
    <span className="HookPhoneInput_prefixValue">
      <FlagIcon className="flag-icon" />
      <span>{prefix}</span>
    </span>
  );

  return (
    <div className={getHookGroupClass(label, 'HookPhoneInput')}>
      {!hideLabel && renderHookInputHeader(name, label, required, inputNote)}
      <Controller
        name={name}
        control={control}
        rules={{
          validate: (value) => validators.validatePhoneNumber(value, required)
        }}
        render={({ field }) => {
          const value = field.value || {
            prefix: SUPPORTED_PREFIX[0],
            number: ''
          };

          return (
            <Space.Compact
              block
              className={`HookPhoneInput ${getHookFieldClass(isTouched, invalid)}`}
            >
              <Select
                optionLabelProp="label"
                onChange={(value) =>
                  field.onChange({
                    prefix: value,
                    number: field.value?.number || ''
                  })
                }
                value={value.prefix}
                getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                classNames={{ popup: { root: 'HookPhoneInputPopup' } }}
                showSearch={{
                  filterOption: (input, option) =>
                    (option?.title?.toLowerCase() || '').indexOf(input.toLowerCase()) >= 0
                }}
                style={{ width: 110 }}
                disabled={disabled}
              >
                {SUPPORTED_PREFIX.map((prefix) => {
                  // Chosing type at runtime https://reactjs.org/docs/jsx-in-depth.html#choosing-the-type-at-runtime
                  const FlagIcon = Flags[prefix.isoCode];
                  const optionLabel = renderPrefixLabel(FlagIcon, prefix.numberPrefix);

                  return (
                    <Select.Option
                      title={prefix.numberPrefix}
                      label={optionLabel}
                      value={prefix.numberPrefix}
                      key={prefix.numberPrefix}
                    >
                      {optionLabel}
                    </Select.Option>
                  );
                })}
              </Select>
              <Input
                id={name}
                onChange={(e) => field.onChange({ ...field.value, number: e.target.value })}
                onBlur={() => {
                  field.onBlur();
                  customOnBlur?.();
                }}
                value={value.number}
                placeholder={placeholder}
                disabled={disabled}
              />
            </Space.Compact>
          );
        }}
      />

      {renderErrorMessage(showError, error?.message)}
    </div>
  );
}

export default PhoneInput;
